import { computed, ref } from "vue"
import { useStore } from "@/store"
import { useIntervalFn, useNow } from "@vueuse/core"
import timeHelpers from "@/helpers/time_helpers"

// Time offset before the session starts to allow onboarding
const BEFORE_SESSION_ONBOARDING_TIME_OFFSET = 15 * 60 * 1000

export function useQuizzesAttemptSummary(quizzesAttemptSummary) {

  const store = useStore()

  const timeAccommodation = computed(() => {
    return store.getters.timeAccommodation(quizzesAttemptSummary.value?.time_accommodation_id)
  })

  const reactiveNowDate = useNow({ interval: 5000 })

  const expectedSessionState = computed(() => {

    const now = reactiveNowDate.value
    const startAt = quizzesAttemptSummary.value?.start_at && new Date(quizzesAttemptSummary.value?.start_at)
    const endAt = quizzesAttemptSummary.value?.end_at && new Date(quizzesAttemptSummary.value?.end_at)

    if (startAt && startAt > now) return "scheduled"
    if (endAt && endAt < now) return "closed"
    if (!startAt) return "not_started"
    return "open"
  })

  /**
   * @description Check if the user is allowed to finish onboarding
   * @return {boolean} true if the user is allowed to finish onboarding
  */

  const allowedToFinishOnboarding = computed(() => {

    if (expectedSessionState.value === "open") return true
    if (expectedSessionState.value === "scheduled") {
      const now = reactiveNowDate.value
      const startAt = quizzesAttemptSummary.value?.start_at && new Date(quizzesAttemptSummary.value?.start_at)
      return startAt && now > startAt - BEFORE_SESSION_ONBOARDING_TIME_OFFSET
    }
    return false
  })


  const timeOffset = store.getters.timeOffset
  const computeRemainingTimeMs = () => {
    if (!quizzesAttemptSummary.value?.submission_deadline_at) return
    return new Date(quizzesAttemptSummary.value?.submission_deadline_at) - Date.now() - timeOffset
  }


  // TODO: check what happens if the submission_deadline_at changes
  useIntervalFn(() => {
    remainingTimeMs.value = computeRemainingTimeMs()
  }, 1000)

  const remainingTimeFormatted = computed(() => {
    return timeHelpers.formatDuration(remainingTimeMs.value)
  })

  const remainingTimeMs = ref(computeRemainingTimeMs())

  const remainingTime = computed(() => {
    return Math.round(remainingTimeMs.value / 1000)
  })

  const countdownProgress = computed(() => {
    const studentStartedAt = new Date(quizzesAttemptSummary.value?.student_started_at)
    const submissionDeadlineAt = new Date(quizzesAttemptSummary.value?.submission_deadline_at)
    const duration = submissionDeadlineAt - studentStartedAt

    return (duration - remainingTimeMs.value) / duration * 100
  })

  return {
    timeAccommodation,
    expectedSessionState,
    remainingTime,
    remainingTimeFormatted,
    countdownProgress,
    allowedToFinishOnboarding,
  }
}
